import { useAtomValue } from "jotai";
import { type FunctionComponent, useEffect, useState } from "react";

import { accountAtom } from "shared/lib/account";

import { usePostiesQuizContext } from "scmp-app/components/posties/quiz/contexts";
import { submitQuiz } from "scmp-app/components/posties/quiz/helpers";
import { PostiesQuizAnswer } from "scmp-app/components/posties/quiz/quiz-answer";
import type { Question as TypeQuestion } from "scmp-app/components/posties/quiz/types";

import IconCross from "./icon-cross.svg";
import {
  ActionButtons,
  Container,
  DisplayMessage,
  DisplayMessageText,
  Question,
  StyledButton,
  StyledIconTick,
  StyledPostiesQuizAnswers,
  Wrapper,
} from "./styles";

type Props = {
  className?: string;
  index: number;
  question: TypeQuestion;
};
export const PostiesQuizQuestion: FunctionComponent<Props> = ({ className, index, question }) => {
  const { user } = useAtomValue(accountAtom);
  const { currentIndex, formId, handleNext, quizStatus, reviewQuestions } = usePostiesQuizContext();
  const [selectedAnswer, setSelectedAnswer] = useState<null | string[]>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [correctAnswer, setCorrectAnswer] = useState<null | string[]>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckAnswer = async () => {
    if (selectedAnswer !== null && !isSubmitting) {
      setIsSubmitting(true);
      try {
        const answers = selectedAnswer?.map(answer => ({
          answer,
          questionId: question.documentId,
        }));

        const submission = await submitQuiz({
          answers,
          email: user?.useremail ?? "",
          formId,
        });

        setIsSubmitting(false);
        if (submission?.success && submission.questionReport.length > 0) {
          setIsCorrect(submission.questionReport[0].isCorrect);
          setCorrectAnswer(submission.questionReport[0].correctAnswers);
        } else {
          return false;
        }
      } catch {
        setIsSubmitting(false);
        return false;
      }
    }
    return;
  };

  const handleContinue = () => {
    handleNext?.();
  };

  const displayMessage = () => {
    let message = null;
    let icon = null;

    if (isCorrect !== null) {
      if (isCorrect === true) {
        message = "That’s right!";
        icon = <StyledIconTick />;
      } else if (isCorrect === false) {
        message = "Check the story again...";
        icon = <IconCross />;
      }

      return (
        <DisplayMessage>
          {icon}
          <DisplayMessageText>{message}</DisplayMessageText>
        </DisplayMessage>
      );
    }

    return <div />;
  };

  const getAnswerType = () => {
    if (question.answerType === "multiple_choice") {
      return question.maxSelect === 1 ? "radio_button" : "multiple_choice";
    }

    return "radio_button";
  };

  useEffect(() => {
    if (quizStatus === "REVIEW") {
      const current = reviewQuestions[currentIndex];

      setSelectedAnswer(current.answers);
      setIsCorrect(current.isCorrect);
      setCorrectAnswer(current.correctAnswers);
    }
    return;
  }, [currentIndex, quizStatus, reviewQuestions]);

  return (
    <Container $isShow={currentIndex === index} className={className}>
      <Wrapper>
        <Question dangerouslySetInnerHTML={{ __html: question.question.html }} />
        <StyledPostiesQuizAnswers>
          {question.options?.map((option, index) => (
            <PostiesQuizAnswer
              answer={option}
              answerType={getAnswerType()}
              correctAnswer={correctAnswer}
              isCorrect={isCorrect}
              key={index}
              selectedAnswer={selectedAnswer}
              setSelectedAnswer={setSelectedAnswer}
            />
          ))}
        </StyledPostiesQuizAnswers>
      </Wrapper>
      <ActionButtons $isCorrect={isCorrect}>
        {displayMessage()}
        {isCorrect === null ? (
          <StyledButton onClick={handleCheckAnswer} variant="button">
            Check
          </StyledButton>
        ) : (
          <StyledButton onClick={handleContinue} variant="button">
            Continue
          </StyledButton>
        )}
      </ActionButtons>
    </Container>
  );
};

PostiesQuizQuestion.displayName = "PostiesQuizQuestion";
